import { useState, useEffect, useCallback, useRef } from "react";

const BACKEND_URL = "";

/**
 * Client Portal Messages — two-way thread between client and Shannon.
 * Client texts: "change LVP to tile", "add ceiling fan", "schedule walk-through".
 * Admin replies in the dashboard. Admin gets email notification on new client msg.
 */
const PortalMessages = ({ projectId = "" }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const scrollerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const url = projectId
        ? `${BACKEND_URL}/api/portal/messages?project_id=${encodeURIComponent(projectId)}`
        : `${BACKEND_URL}/api/portal/messages`;
      const r = await fetch(url, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setMessages(d.messages || []);
      }
    } catch {}
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    // auto-scroll to bottom on new messages
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    setError("");
    if (!body.trim()) return;
    setSending(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/portal/messages`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: projectId, body: body.trim() })
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) {
        setBody("");
        await load();
      } else {
        setError(d.detail || "Could not send. Try again.");
      }
    } catch {
      setError("Network error. Try again.");
    }
    setSending(false);
  };

  const fmtTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  return (
    <div data-testid="portal-messages" style={{ background: "#fff", borderRadius: 10, padding: "1.25rem 1.5rem", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", border: "1px solid #e5e7eb" }}>
      <div style={{ marginBottom: "0.85rem" }}>
        <h2 style={{ fontSize: "1.1rem", fontWeight: 800, color: "#1a1a1a", margin: "0 0 0.25rem" }}>💬 Message Moovin Forward</h2>
        <p style={{ fontSize: "0.82rem", color: "#64748b", margin: 0, lineHeight: 1.5 }}>
          Want to swap out a finish, add an item from the package, or schedule a walk-through? Send a message — Shannon will reply directly. We&rsquo;re a small team, so expect a personal answer the same day.
        </p>
      </div>

      <div
        ref={scrollerRef}
        data-testid="portal-messages-scroller"
        style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "0.85rem", maxHeight: 320, minHeight: 140, overflowY: "auto", marginBottom: "0.75rem" }}
      >
        {loading ? (
          <p style={{ color: "#94a3b8", fontSize: "0.82rem", textAlign: "center", margin: "1rem 0" }}>Loading…</p>
        ) : messages.length === 0 ? (
          <p style={{ color: "#94a3b8", fontSize: "0.82rem", textAlign: "center", margin: "1rem 0", fontStyle: "italic" }}>
            No messages yet. Send your first one below.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {messages.map(m => {
              const mine = m.sender_role === "client";
              return (
                <div
                  key={m.message_id}
                  data-testid={`msg-${m.message_id}`}
                  style={{ alignSelf: mine ? "flex-end" : "flex-start", maxWidth: "82%" }}
                >
                  <div style={{ background: mine ? "#0d9488" : "#fff", color: mine ? "#fff" : "#1a1a1a", padding: "0.55rem 0.85rem", borderRadius: 12, border: mine ? "none" : "1px solid #e5e7eb", fontSize: "0.88rem", lineHeight: 1.45, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {m.body}
                  </div>
                  <div style={{ fontSize: "0.66rem", color: "#94a3b8", marginTop: "0.18rem", textAlign: mine ? "right" : "left" }}>
                    {mine ? "You" : (m.sender_name || "Moovin Forward")} · {fmtTime(m.created_at)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: "0.55rem", alignItems: "flex-end" }}>
        <textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) send(); }}
          placeholder="Type your message… (Ctrl/Cmd+Enter to send)"
          rows={2}
          data-testid="portal-message-input"
          style={{ flex: 1, padding: "0.65rem 0.85rem", borderRadius: 6, border: "1px solid #d1d5db", fontSize: "0.88rem", fontFamily: "inherit", resize: "vertical", minHeight: 56 }}
        />
        <button
          onClick={send}
          disabled={!body.trim() || sending}
          data-testid="portal-message-send"
          style={{ background: body.trim() && !sending ? "#0d9488" : "#cbd5e1", color: "#fff", border: "none", padding: "0.7rem 1.2rem", borderRadius: 6, fontWeight: 700, fontSize: "0.82rem", cursor: body.trim() && !sending ? "pointer" : "not-allowed", whiteSpace: "nowrap" }}
        >
          {sending ? "Sending…" : "Send"}
        </button>
      </div>
      {error && (
        <p data-testid="portal-message-error" style={{ color: "#dc2626", fontSize: "0.78rem", margin: "0.45rem 0 0" }}>✗ {error}</p>
      )}
    </div>
  );
};

export default PortalMessages;
